import axios from "axios";
import type { AxiosInstance } from "axios";
import { z } from "zod";
import { isTutorialActive } from "../lib/tutorials/types/window";
import { useForceUpdateStore } from "../stores/forceUpdate";
import {
  AssignmentResponseSchema,
  AssignmentCriterionSchema,
  CreateAssignmentInputSchema,
  ListAssignmentsResponseSchema,
  DocumentListSchema,
  DocumentSchema,
  CreateDocumentInputSchema,
  UpdateDocumentInputSchema,
  ShareDocumentSchema,
  UpdateAssignmentInputSchema,
  BillingCheckoutSessionSchema,
  BillingPortalSessionSchema,
  UsageSummarySchema,
  ClassResponseSchema,
  CreateClassRequestSchema,
  UpdateClassRequestSchema,
  ListClassesResponseSchema,
  StudentResponseSchema,
  CreateStudentRequestSchema,
  UpdateStudentRequestSchema,
  ListStudentsResponseSchema,
  CriteriaGenerateResponseSchema,
  CriteriaBulkCreateResponseSchema,
  CriteriaTemplateGenerateResponseSchema,
  AuthUserSchema,
  FeedbackItemSchema,
  FeedbackListResponseSchema,
  CreateFeedbackInputSchema,
  LogListResponseSchema,
  CriteriaTemplateListSchema,
  CriteriaTemplateWithCriteriaSchema,
  CriteriaTemplateSchema,
  CreateCriteriaTemplateInputSchema,
  UpdateCriteriaTemplateInputSchema,
  ApplyCriteriaTemplateInputSchema,
  SaveAsCriteriaTemplateInputSchema,
  AssignmentCriteriaSchema,
  TemplateListSchema,
  EmailTemplateSchema,
  EmailTemplateListSchema,
  CreateEmailTemplateInputSchema,
  UpdateEmailTemplateInputSchema,
  RenderEmailResponseSchema,
  StudentPortalResponseSchema,
  PortalProgressionResponseSchema,
  PortalAssignmentWritingResponseSchema,
  PortalCreateDocumentResponseSchema,
  PortalDocumentVersionSchema,
  PortalVersionSubmitResponseSchema,
  EditEventBatchUploadResponseSchema,
  type EditEventBatchUploadInput,
  type EditEventBatchUploadResponse,
  UpgradeToAnnualResponseSchema,
  MessageResponseSchema,
  FeedbackAttachmentResponseSchema,
  FeedbackAttachmentUrlResponseSchema,
  EmailListResponseSchema,
  GCStatusSchema,
  GCCourseSchema,
  GCClassOutSchema,
  GCSyncResultSchema,
  EditMetricsResponseSchema,
  ExtensionDocumentListResponseSchema,
  ExtensionDocumentDetailResponseSchema,
  DeleteCountResponseSchema,
  ExtensionEditEventsResponseSchema,
  ExtensionLicenseStatusResponseSchema,
  StreamingTokenResponseSchema,
} from "@markeroo/shared";
import type {
  ApiClient,
  ApiPagination,
  AssignmentCriterion,
  AuthUser,
  CreateAssignmentInput,
  CreateDocumentInput,
  DocumentList,
  UpdateAssignmentInput,
  UpdateDocumentInput,
  UsageSummary,
  CreateClassInput,
  UpdateClassInput,
  PaginatedClasses,
  Class,
  CreateStudentInput,
  UpdateStudentInput,
  Student,
  PaginatedStudents,
  FeedbackItem,
  FeedbackListResponse,
  CreateFeedbackInput,
  LogListResponse,
  CriteriaTemplate,
  CriteriaTemplateWithCriteria,
  CriteriaTemplateList,
  CriteriaTemplateGenerateResponse,
  CreateCriteriaTemplateInput,
  UpdateCriteriaTemplateInput,
  ApplyCriteriaTemplateInput,
  SaveAsCriteriaTemplateInput,
  PaperType,
  TemplateList,
  EmailListResponse,
  EmailTemplate,
  EmailTemplateList,
  CreateEmailTemplateInput,
  UpdateEmailTemplateInput,
  RenderEmailResponse,
  StudentPortalResponse,
  ShareDocument,
  UpdateCompletedHighlightsInput,
  PortalProgressionResponse,
  PortalAssignmentWritingResponse,
  PortalCreateDocumentResponse,
  PortalDocumentVersion,
  PortalVersionUpdateInput,
  PortalVersionSubmitResponse,
  GCCourse,
  GCClassOut,
  EditMetricsResponse,
  ExtensionDocumentListResponse,
  ExtensionDocumentDetailResponse,
  ExtensionEditEventsResponse,
  ExtensionLicenseStatusResponse,
} from "@markeroo/shared";

export class HttpApiClient implements ApiClient {
  private readonly http: AxiosInstance;
  private readonly tokenProvider: () => string | null;

  constructor({ baseURL, tokenProvider }: { baseURL: string; tokenProvider: () => string | null }) {
    this.tokenProvider = tokenProvider;
    this.http = axios.create({
      baseURL,
      withCredentials: true,
    });

    // Add response interceptor for force update header
    this.http.interceptors.response.use(
      (response) => {
        const forceUpdateHeader = response.headers["x-force-update-at"] as string | undefined;
        if (forceUpdateHeader) {
          const timestamp = parseInt(forceUpdateHeader, 10);
          if (!isNaN(timestamp)) {
            useForceUpdateStore.getState().checkForUpdate(timestamp);
          }
        }
        return response;
      },
      (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.headers?.["x-force-update-at"]) {
          const headerValue = error.response.headers["x-force-update-at"] as string;
          const timestamp = parseInt(headerValue, 10);
          if (!isNaN(timestamp)) {
            useForceUpdateStore.getState().checkForUpdate(timestamp);
          }
        }
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }
    );
  }

  isAuthenticated(): boolean {
    return this.tokenProvider() !== null;
  }

  private authHeaders(): Record<string, string> {
    const token = this.tokenProvider();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private authHeadersWithTutorial(): Record<string, string> {
    const headers = this.authHeaders();
    if (isTutorialActive()) {
      headers['X-Tutorial-Active'] = 'true';
    }
    return headers;
  }

  async listAssignments(params?: ApiPagination & { include_archived?: boolean; class_id?: string }) {
    const response = await this.http.get("/assignments", {
      params,
      headers: this.authHeaders(),
    });
    return ListAssignmentsResponseSchema.parse(response.data);
  }

  async getAssignment(id: string) {
    const response = await this.http.get(`/assignments/${id}`, { headers: this.authHeaders() });
    return AssignmentResponseSchema.parse(response.data);
  }

  async createAssignment(payload: CreateAssignmentInput) {
    const parsed = CreateAssignmentInputSchema.parse(payload);
    const response = await this.http.post("/assignments", parsed, { headers: this.authHeaders() });
    return AssignmentResponseSchema.parse(response.data);
  }

  async updateAssignment(id: string, payload: UpdateAssignmentInput) {
    const parsed = UpdateAssignmentInputSchema.parse(payload);
    const response = await this.http.patch(`/assignments/${id}`, parsed, { headers: this.authHeaders() });
    return AssignmentResponseSchema.parse(response.data);
  }

  async deleteAssignment(id: string): Promise<void> {
    await this.http.delete(`/assignments/${id}`, { headers: this.authHeaders() });
  }

  async deleteAssignmentForever(id: string): Promise<void> {
    await this.http.delete(`/assignments/${id}/delete-forever`, { headers: this.authHeaders() });
  }

  async assessDocument(assignmentId: string, documentId: string): Promise<void> {
    await this.http.post(`/assignments/${assignmentId}/documents/${documentId}/assess`, undefined, { headers: this.authHeaders() });
  }

  async listAssignmentDocuments(assignmentId: string): Promise<DocumentList> {
    const response = await this.http.get(`/assignments/${assignmentId}/documents`, {
      headers: this.authHeaders(),
    });
    return DocumentListSchema.parse(response.data);
  }

  async createDocument(assignmentId: string, payload: CreateDocumentInput) {
    const parsed = CreateDocumentInputSchema.parse(payload);
    const response = await this.http.post(
      `/assignments/${assignmentId}/documents`,
      parsed,
      { headers: this.authHeadersWithTutorial() }
    );
    return DocumentSchema.parse(response.data);
  }

  async updateDocument(
    assignmentId: string,
    documentId: string,
    payload: UpdateDocumentInput
  ) {
    const parsed = UpdateDocumentInputSchema.parse(payload);
    const response = await this.http.patch(
      `/assignments/${assignmentId}/documents/${documentId}`,
      parsed,
      { headers: this.authHeaders() }
    );
    return DocumentSchema.parse(response.data);
  }

  async deleteDocument(assignmentId: string, documentId: string): Promise<void> {
    await this.http.delete(
      `/assignments/${assignmentId}/documents/${documentId}`,
      { headers: this.authHeaders() }
    );
  }

  async getUsage(): Promise<UsageSummary> {
    const response = await this.http.get("/billing/usage", {
      headers: this.authHeaders(),
    });
    return UsageSummarySchema.parse(response.data);
  }

  async createCheckoutSession(plan: "monthly" | "annual" = "monthly") {
    const response = await this.http.post(
      "/billing/checkout-session",
      { plan },
      { headers: this.authHeaders() }
    );
    return BillingCheckoutSessionSchema.parse(response.data);
  }

  async createPortalSession() {
    const response = await this.http.post(
      "/billing/portal-session",
      undefined,
      { headers: this.authHeaders() }
    );
    return BillingPortalSessionSchema.parse(response.data);
  }

  async upgradeToAnnual(): Promise<{ success: boolean; message: string }> {
    const response = await this.http.post(
      "/billing/upgrade-to-annual",
      undefined,
      { headers: this.authHeaders() }
    );
    return UpgradeToAnnualResponseSchema.parse(response.data);
  }

  async getSharedDocument(token: string) {
    const response = await this.http.get(`/share/${token}`);
    // Return raw data - let the hook handle parsing with ShareWithProgressionSchema
    return response.data as unknown;
  }

  async createShareLink(assignmentId: string, documentId: string) {
    const response = await this.http.post(
      `/assignments/${assignmentId}/documents/${documentId}/share`,
      undefined,
      { headers: this.authHeaders() }
    );
    return DocumentSchema.parse(response.data);
  }

  async revokeShareLink(assignmentId: string, documentId: string) {
    const response = await this.http.delete(
      `/assignments/${assignmentId}/documents/${documentId}/share`,
      { headers: this.authHeaders() }
    );
    return DocumentSchema.parse(response.data);
  }

  async getDocumentMarkup(assignmentId: string, documentId: string) {
    const response = await this.http.get(
      `/assignments/${assignmentId}/documents/${documentId}/markup`,
      { headers: this.authHeaders() }
    );
    return ShareDocumentSchema.parse(response.data);
  }

  async updateDocumentSummary(
    assignmentId: string,
    documentId: string,
    payload: { celebrate?: string; priorities?: string; strategies?: string }
  ) {
    const response = await this.http.patch(
      `/assignments/${assignmentId}/documents/${documentId}/markup/summary`,
      payload,
      { headers: this.authHeaders() }
    );
    return ShareDocumentSchema.parse(response.data);
  }

  async resetDocumentSummaryField(
    assignmentId: string,
    documentId: string,
    field: "celebrate" | "priorities" | "strategies"
  ) {
    const response = await this.http.post(
      `/assignments/${assignmentId}/documents/${documentId}/markup/summary/reset?field=${field}`,
      undefined,
      { headers: this.authHeaders() }
    );
    return ShareDocumentSchema.parse(response.data);
  }

  async deleteDocumentHighlight(
    assignmentId: string,
    documentId: string,
    highlightId: string
  ) {
    const response = await this.http.delete(
      `/assignments/${assignmentId}/documents/${documentId}/markup/highlights/${highlightId}`,
      { headers: this.authHeaders() }
    );
    return ShareDocumentSchema.parse(response.data);
  }

  async updateDocumentShowAllMarkup(
    assignmentId: string,
    documentId: string,
    showAllMarkup: boolean
  ) {
    const response = await this.http.patch(
      `/assignments/${assignmentId}/documents/${documentId}/show-all-markup`,
      { show_all_markup: showAllMarkup },
      { headers: this.authHeaders() }
    );
    return ShareDocumentSchema.parse(response.data);
  }

  async createTeacherComment(
    assignmentId: string,
    documentId: string,
    payload: {
      start_path: number[];
      start_char: number;
      end_path: number[];
      end_char: number;
      comment_text: string;
      category: string;
      phrase: string;
      before_context?: string | null;
      after_context?: string | null;
    }
  ) {
    const response = await this.http.post(
      `/assignments/${assignmentId}/documents/${documentId}/teacher-comments`,
      payload,
      { headers: this.authHeaders() }
    );
    return ShareDocumentSchema.parse(response.data);
  }

  async updateTeacherComment(
    assignmentId: string,
    documentId: string,
    commentId: string,
    payload: {
      comment_text: string;
      category?: string;
    }
  ) {
    const response = await this.http.patch(
      `/assignments/${assignmentId}/documents/${documentId}/teacher-comments/${commentId}`,
      payload,
      { headers: this.authHeaders() }
    );
    return ShareDocumentSchema.parse(response.data);
  }

  async deleteTeacherComment(
    assignmentId: string,
    documentId: string,
    commentId: string
  ) {
    const response = await this.http.delete(
      `/assignments/${assignmentId}/documents/${documentId}/teacher-comments/${commentId}`,
      { headers: this.authHeaders() }
    );
    return ShareDocumentSchema.parse(response.data);
  }

  async deleteCriterion(assignmentId: string, criterionKey: string): Promise<void> {
    await this.http.delete(
      `/assignments/${assignmentId}/criteria/${criterionKey}`,
      { headers: this.authHeaders() }
    );
  }

  async createCriterion(
    assignmentId: string,
    payload: { label: string; llmDescription: string; humanDescription: string; category?: string }
  ): Promise<AssignmentCriterion> {
    const response = await this.http.post(
      `/assignments/${assignmentId}/criteria`,
      {
        label: payload.label,
        llm_description: payload.llmDescription,
        human_description: payload.humanDescription,
        category: payload.category,
      },
      { headers: this.authHeaders() }
    );
    return AssignmentCriterionSchema.parse(response.data);
  }

  async updateCriterion(
    assignmentId: string,
    criterionKey: string,
    payload: { label?: string; llmDescription?: string; humanDescription?: string; category?: string }
  ): Promise<AssignmentCriterion> {
    const body: Record<string, string> = {};
    if (payload.label !== undefined) body.label = payload.label;
    if (payload.llmDescription !== undefined) body.llmDescription = payload.llmDescription;
    if (payload.humanDescription !== undefined) body.humanDescription = payload.humanDescription;
    if (payload.category !== undefined) body.category = payload.category;

    const response = await this.http.patch(
      `/assignments/${assignmentId}/criteria/${criterionKey}`,
      body,
      { headers: this.authHeaders() }
    );
    return AssignmentCriterionSchema.parse(response.data);
  }

  async generateCriteria(
    assignmentId: string,
    payload: { prompt: string; gradeLevel?: number | null }
  ): Promise<{ criteria: AssignmentCriterion[] }> {
    const response = await this.http.post(
      `/assignments/${assignmentId}/criteria/generate`,
      { prompt: payload.prompt, gradeLevel: payload.gradeLevel },
      { headers: this.authHeaders() }
    );
    return CriteriaGenerateResponseSchema.parse(response.data);
  }

  async bulkCreateCriteria(
    assignmentId: string,
    payload: {
      criteria: Array<{
        label: string;
        llmDescription: string;
        humanDescription: string;
        action?: string | null;
        key?: string | null;
        category?: string;
      }>;
    }
  ): Promise<{
    created: AssignmentCriterion[];
    failed: Array<{ index: number; label: string; error: string }>;
  }> {
    const response = await this.http.post(
      `/assignments/${assignmentId}/criteria/bulk`,
      {
        criteria: payload.criteria.map(c => ({
          label: c.label,
          llm_description: c.llmDescription,
          human_description: c.humanDescription,
          action: c.action,
          key: c.key,
          category: c.category,
        })),
      },
      { headers: this.authHeadersWithTutorial() }
    );
    return CriteriaBulkCreateResponseSchema.parse(response.data);
  }

  async cleanupTutorialResources(): Promise<void> {
    await this.http.delete(
      `/assignments/tutorial-resources`,
      {
        headers: this.authHeaders(),
      }
    );
  }

  async resetCriteriaDefaults(assignmentId: string): Promise<AssignmentCriterion[]> {
    const response = await this.http.post(
      `/assignments/${assignmentId}/criteria/reset-defaults`,
      undefined,
      { headers: this.authHeaders() }
    );
    return z.array(AssignmentCriterionSchema).parse(response.data);
  }

  async listClasses(params?: ApiPagination): Promise<PaginatedClasses> {
    const response = await this.http.get("/classes", {
      params,
      headers: this.authHeaders(),
    });
    return ListClassesResponseSchema.parse(response.data);
  }

  async getClass(id: string): Promise<Class> {
    const response = await this.http.get(`/classes/${id}`, {
      headers: this.authHeaders(),
    });
    return ClassResponseSchema.parse(response.data);
  }

  async createClass(payload: CreateClassInput): Promise<Class> {
    const parsed = CreateClassRequestSchema.parse(payload);
    const response = await this.http.post("/classes", parsed, {
      headers: this.authHeaders(),
    });
    return ClassResponseSchema.parse(response.data);
  }

  async updateClass(id: string, payload: UpdateClassInput): Promise<Class> {
    const parsed = UpdateClassRequestSchema.parse(payload);
    const response = await this.http.patch(`/classes/${id}`, parsed, {
      headers: this.authHeaders(),
    });
    return ClassResponseSchema.parse(response.data);
  }

  async listStudents(classId: string, params?: ApiPagination): Promise<PaginatedStudents> {
    const response = await this.http.get(`/classes/${classId}/students`, {
      params,
      headers: this.authHeaders(),
    });
    return ListStudentsResponseSchema.parse(response.data);
  }

  async getStudent(classId: string, studentId: string): Promise<Student> {
    const response = await this.http.get(`/classes/${classId}/students/${studentId}`, {
      headers: this.authHeaders(),
    });
    return StudentResponseSchema.parse(response.data);
  }

  async createStudent(classId: string, payload: CreateStudentInput): Promise<Student> {
    const parsed = CreateStudentRequestSchema.parse(payload);
    const response = await this.http.post(`/classes/${classId}/students`, parsed, {
      headers: this.authHeadersWithTutorial(),
    });
    return StudentResponseSchema.parse(response.data);
  }

  async updateStudent(
    classId: string,
    studentId: string,
    payload: UpdateStudentInput
  ): Promise<Student> {
    const parsed = UpdateStudentRequestSchema.parse(payload);
    const response = await this.http.patch(
      `/classes/${classId}/students/${studentId}`,
      parsed,
      {
        headers: this.authHeaders(),
      }
    );
    return StudentResponseSchema.parse(response.data);
  }

  async deleteStudent(classId: string, studentId: string): Promise<void> {
    await this.http.delete(`/classes/${classId}/students/${studentId}`, {
      headers: this.authHeaders(),
    });
  }

  async deleteStudentForever(classId: string, studentId: string): Promise<void> {
    await this.http.delete(`/classes/${classId}/students/${studentId}/delete-forever`, {
      headers: this.authHeaders(),
    });
  }

  async deleteClassForever(classId: string): Promise<void> {
    await this.http.delete(`/classes/${classId}/delete-forever`, {
      headers: this.authHeaders(),
    });
  }

  async updateUserPreferences(preferences: Record<string, unknown>) {
    const response = await this.http.patch(
      "/auth/me",
      { preferences },
      { headers: this.authHeaders() }
    );
    return AuthUserSchema.parse(response.data);
  }

  async updatePreferredName(preferredName: string | null): Promise<AuthUser> {
    const response = await this.http.patch(
      "/auth/me/preferred-name",
      { preferredName },
      { headers: this.authHeaders() }
    );
    return AuthUserSchema.parse(response.data);
  }

  async updateStudentWritingEnabled(enabled: boolean): Promise<AuthUser> {
    const response = await this.http.patch(
      "/auth/me/student-writing",
      { studentWritingEnabled: enabled },
      { headers: this.authHeaders() }
    );
    return AuthUserSchema.parse(response.data);
  }

  async updateTutorialPreferences(
    tutorialId: string,
    updates: Record<string, unknown>
  ): Promise<AuthUser> {
    const response = await this.http.patch(
      "/auth/me/tutorials",
      { tutorialId, updates },
      { headers: this.authHeaders() }
    );
    return AuthUserSchema.parse(response.data);
  }

  async deleteAccount(): Promise<void> {
    await this.http.delete("/auth/me", { headers: this.authHeaders() });
  }

  async submitFeedback(payload: CreateFeedbackInput): Promise<FeedbackItem> {
    const parsed = CreateFeedbackInputSchema.parse(payload);
    const response = await this.http.post(
      "/feedback",
      parsed,
      { headers: this.authHeaders() }
    );
    return FeedbackItemSchema.parse(response.data);
  }

  async listFeedback(params?: { status?: string; limit?: number; cursor?: string }): Promise<FeedbackListResponse> {
    const response = await this.http.get("/admin/feedback", {
      params,
      headers: this.authHeaders(),
    });
    return FeedbackListResponseSchema.parse(response.data);
  }

  async getFeedback(id: string): Promise<FeedbackItem> {
    const response = await this.http.get(`/admin/feedback/${id}`, {
      headers: this.authHeaders(),
    });
    return FeedbackItemSchema.parse(response.data);
  }

  async updateFeedbackStatus(id: string, payload: { status?: string; adminNotes?: string }): Promise<FeedbackItem> {
    const response = await this.http.patch(`/admin/feedback/${id}`, payload, {
      headers: this.authHeaders(),
    });
    return FeedbackItemSchema.parse(response.data);
  }

  async deleteFeedback(id: string): Promise<void> {
    await this.http.delete(`/admin/feedback/${id}`, {
      headers: this.authHeaders(),
    });
  }

  async uploadFeedbackAttachment(
    feedbackId: string,
    file: File
  ): Promise<{ id: string; filename: string; fileSize: number; contentType: string; createdAt: string }> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await this.http.post(`/feedback/${feedbackId}/attachments`, formData, {
      headers: {
        ...this.authHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });

    return FeedbackAttachmentResponseSchema.parse(response.data);
  }

  async getFeedbackAttachmentUrl(attachmentId: string): Promise<{ url: string }> {
    const response = await this.http.get(`/feedback/attachments/${attachmentId}/url`, {
      headers: this.authHeaders(),
    });
    return FeedbackAttachmentUrlResponseSchema.parse(response.data);
  }

  async replyToFeedback(id: string, message: string): Promise<void> {
    await this.http.post(`/admin/feedback/${id}/reply`, { message }, {
      headers: this.authHeaders(),
    });
  }

  async listFeedbackEmails(feedbackId: string): Promise<EmailListResponse> {
    const response = await this.http.get(`/admin/feedback/${feedbackId}/emails`, {
      headers: this.authHeaders(),
    });
    return EmailListResponseSchema.parse(response.data);
  }

  async listLogs(params?: {
    level?: string;
    service?: string;
    search?: string;
    startTime?: string;
    endTime?: string;
    limit?: number;
    cursor?: string;
  }): Promise<LogListResponse> {
    const queryParams: Record<string, string | number> = {};
    if (params?.level) queryParams.level = params.level;
    if (params?.service) queryParams.service = params.service;
    if (params?.search) queryParams.search = params.search;
    if (params?.startTime) queryParams.start_time = params.startTime;
    if (params?.endTime) queryParams.end_time = params.endTime;
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.cursor) queryParams.cursor = params.cursor;

    const response = await this.http.get("/admin/logs", {
      params: queryParams,
      headers: this.authHeaders(),
    });
    return LogListResponseSchema.parse(response.data);
  }

  async listCriteriaTemplates(params?: {
    paperType?: PaperType | null;
    grades?: number[] | null;
    includeArchived?: boolean;
  }): Promise<CriteriaTemplateList> {
    const queryParams: Record<string, string | number | boolean> = {};
    if (params?.paperType) queryParams.paperType = params.paperType;
    if (params?.grades) queryParams.grades = params.grades.join(",");
    if (params?.includeArchived) queryParams.includeArchived = params.includeArchived;

    const response = await this.http.get("/criteria-templates", {
      params: queryParams,
      headers: this.authHeaders(),
    });
    return CriteriaTemplateListSchema.parse(response.data);
  }

  async getCriteriaTemplate(id: string): Promise<CriteriaTemplateWithCriteria> {
    const response = await this.http.get(`/criteria-templates/${id}`, {
      headers: this.authHeaders(),
    });
    return CriteriaTemplateWithCriteriaSchema.parse(response.data);
  }

  async createCriteriaTemplate(payload: CreateCriteriaTemplateInput): Promise<CriteriaTemplateWithCriteria> {
    const parsed = CreateCriteriaTemplateInputSchema.parse(payload);
    const response = await this.http.post("/criteria-templates", parsed, {
      headers: this.authHeaders(),
    });
    return CriteriaTemplateWithCriteriaSchema.parse(response.data);
  }

  async updateCriteriaTemplate(id: string, payload: UpdateCriteriaTemplateInput): Promise<CriteriaTemplate> {
    const parsed = UpdateCriteriaTemplateInputSchema.parse(payload);
    const response = await this.http.patch(`/criteria-templates/${id}`, parsed, {
      headers: this.authHeaders(),
    });
    return CriteriaTemplateSchema.parse(response.data);
  }

  async deleteCriteriaTemplate(id: string): Promise<void> {
    await this.http.delete(`/criteria-templates/${id}`, {
      headers: this.authHeaders(),
    });
  }

  async applyCriteriaTemplateToAssignment(
    assignmentId: string,
    payload: ApplyCriteriaTemplateInput
  ): Promise<AssignmentCriterion[]> {
    const parsed = ApplyCriteriaTemplateInputSchema.parse(payload);
    const response = await this.http.post(
      `/assignments/${assignmentId}/apply-criteria-template`,
      parsed,
      { headers: this.authHeaders() }
    );
    return AssignmentCriteriaSchema.parse(response.data);
  }

  async saveAssignmentCriteriaAsCriteriaTemplate(
    assignmentId: string,
    payload: SaveAsCriteriaTemplateInput
  ): Promise<CriteriaTemplateWithCriteria> {
    const parsed = SaveAsCriteriaTemplateInputSchema.parse(payload);
    const response = await this.http.post(
      `/assignments/${assignmentId}/save-criteria-template`,
      parsed,
      { headers: this.authHeaders() }
    );
    return CriteriaTemplateWithCriteriaSchema.parse(response.data);
  }

  async listAllTemplates(grade?: number): Promise<TemplateList> {
    const params = grade ? { grade } : {};
    const response = await this.http.get("/templates", {
      params,
      headers: this.authHeaders(),
    });
    return TemplateListSchema.parse(response.data);
  }

  async duplicateCriteriaTemplate(id: string): Promise<CriteriaTemplateWithCriteria> {
    const response = await this.http.post(
      `/criteria-templates/${id}/duplicate`,
      undefined,
      { headers: this.authHeaders() }
    );
    return CriteriaTemplateWithCriteriaSchema.parse(response.data);
  }

  async createCriteriaTemplateItem(
    criteriaTemplateId: string,
    payload: { label: string; llmDescription: string; humanDescription: string; category?: string }
  ): Promise<unknown> {
    const response = await this.http.post(
      `/criteria-templates/${criteriaTemplateId}/criteria`,
      {
        label: payload.label,
        llmDescription: payload.llmDescription,
        humanDescription: payload.humanDescription,
        category: payload.category,
      },
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  async updateCriteriaTemplateItem(
    criteriaTemplateId: string,
    criterionKey: string,
    payload: { label?: string; llmDescription?: string; humanDescription?: string; category?: string }
  ): Promise<unknown> {
    const body: Record<string, string> = {};
    if (payload.label !== undefined) body.label = payload.label;
    if (payload.llmDescription !== undefined) body.llmDescription = payload.llmDescription;
    if (payload.humanDescription !== undefined) body.humanDescription = payload.humanDescription;
    if (payload.category !== undefined) body.category = payload.category;

    const response = await this.http.patch(
      `/criteria-templates/${criteriaTemplateId}/criteria/${criterionKey}`,
      body,
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  async deleteCriteriaTemplateItem(criteriaTemplateId: string, criterionKey: string): Promise<void> {
    await this.http.delete(`/criteria-templates/${criteriaTemplateId}/criteria/${criterionKey}`, {
      headers: this.authHeaders(),
    });
  }

  async generateCriteriaTemplateItems(
    criteriaTemplateId: string,
    payload: { prompt: string; gradeLevel?: number | null }
  ): Promise<CriteriaTemplateGenerateResponse> {
    const response = await this.http.post(
      `/criteria-templates/${criteriaTemplateId}/criteria/generate`,
      {
        prompt: payload.prompt,
        gradeLevel: payload.gradeLevel,
      },
      { headers: this.authHeaders() }
    );
    return CriteriaTemplateGenerateResponseSchema.parse(response.data);
  }

  // Vocabulary Preferences API
  async getAllVocabulary(): Promise<unknown> {
    const response = await this.http.get("/preferences/vocabulary", {
      headers: this.authHeaders(),
    });
    return response.data;
  }

  async getGradeVocabulary(grade: string): Promise<unknown> {
    const response = await this.http.get(`/preferences/vocabulary/${grade}`, {
      headers: this.authHeaders(),
    });
    return response.data;
  }

  async updateGradeVocabulary(
    grade: string,
    payload: {
      added_words_students_should_know: string[];
      removed_words_students_should_know: string[];
      added_words_students_should_avoid: string[];
      removed_words_students_should_avoid: string[];
    }
  ): Promise<unknown> {
    const response = await this.http.put(`/preferences/vocabulary/${grade}`, payload, {
      headers: this.authHeaders(),
    });
    return response.data;
  }

  async resetGradeVocabulary(grade: string): Promise<unknown> {
    const response = await this.http.post(
      `/preferences/vocabulary/${grade}/reset`,
      undefined,
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  async resetAllVocabulary(): Promise<unknown> {
    const response = await this.http.post("/preferences/vocabulary/reset-all", undefined, {
      headers: this.authHeaders(),
    });
    return response.data;
  }

  async getStreamingToken(): Promise<{ token: string; expiresIn: number }> {
    const response = await this.http.post(
      "/voice/token",
      undefined,
      { headers: this.authHeaders() }
    );
    return StreamingTokenResponseSchema.parse(response.data);
  }

  // Email Template API (user-level)
  async listEmailTemplates(): Promise<EmailTemplateList> {
    const response = await this.http.get("/email-templates", {
      headers: this.authHeaders(),
    });
    return EmailTemplateListSchema.parse(response.data);
  }

  async getEmailTemplate(templateId: string): Promise<EmailTemplate> {
    const response = await this.http.get(`/email-templates/${templateId}`, {
      headers: this.authHeaders(),
    });
    return EmailTemplateSchema.parse(response.data);
  }

  async createEmailTemplate(
    payload: CreateEmailTemplateInput
  ): Promise<EmailTemplate> {
    const parsed = CreateEmailTemplateInputSchema.parse(payload);
    const response = await this.http.post("/email-templates", parsed, {
      headers: this.authHeaders(),
    });
    return EmailTemplateSchema.parse(response.data);
  }

  async updateEmailTemplate(
    templateId: string,
    payload: UpdateEmailTemplateInput
  ): Promise<EmailTemplate> {
    const parsed = UpdateEmailTemplateInputSchema.parse(payload);
    const response = await this.http.patch(
      `/email-templates/${templateId}`,
      parsed,
      { headers: this.authHeaders() }
    );
    return EmailTemplateSchema.parse(response.data);
  }

  async deleteEmailTemplate(templateId: string): Promise<void> {
    await this.http.delete(`/email-templates/${templateId}`, {
      headers: this.authHeaders(),
    });
  }

  async renderDocumentEmail(
    assignmentId: string,
    documentId: string,
    templateId?: string
  ): Promise<RenderEmailResponse> {
    const params = templateId ? { template_id: templateId } : {};
    const response = await this.http.get(
      `/assignments/${assignmentId}/documents/${documentId}/email/render`,
      {
        params,
        headers: this.authHeaders(),
      }
    );
    return RenderEmailResponseSchema.parse(response.data);
  }

  // Google Classroom API
  async getGCStatus(): Promise<{
    gc_mode: boolean;
    gc_mode_enabled_at: string | null;
    has_refresh_token: boolean;
    has_write_scopes: boolean;
    has_ejected_data: boolean;
  }> {
    const response = await this.http.get("/google-classroom/status", {
      headers: this.authHeaders(),
    });
    return GCStatusSchema.parse(response.data);
  }

  async disconnectGC(): Promise<{ message: string }> {
    const response = await this.http.post(
      "/google-classroom/disconnect",
      undefined,
      { headers: this.authHeaders() }
    );
    return MessageResponseSchema.parse(response.data);
  }

  async listGCCourses(): Promise<GCCourse[]> {
    const response = await this.http.get("/google-classroom/courses", {
      headers: this.authHeaders(),
    });
    return z.array(GCCourseSchema).parse(response.data);
  }

  async importGCCourse(courseId: string): Promise<GCClassOut> {
    const response = await this.http.post(
      `/google-classroom/courses/${courseId}/import`,
      undefined,
      { headers: this.authHeaders() }
    );
    return GCClassOutSchema.parse(response.data);
  }

  async syncClass(classId: string): Promise<{
    students: number;
    assignments: number;
    submissions: number;
  }> {
    const response = await this.http.post(
      `/google-classroom/classes/${classId}/sync`,
      undefined,
      { headers: this.authHeaders() }
    );
    return GCSyncResultSchema.parse(response.data);
  }

  // ==========================================================================
  // Student Portal API (public - no auth required)
  // ==========================================================================

  async getStudentPortal(portalKey: string): Promise<StudentPortalResponse> {
    const response = await this.http.get(`/portal/${portalKey}`);
    return StudentPortalResponseSchema.parse(response.data);
  }

  async getPortalPaper(portalKey: string, documentId: string): Promise<ShareDocument> {
    const response = await this.http.get(`/portal/${portalKey}/paper/${documentId}`);
    return ShareDocumentSchema.parse(response.data);
  }

  async getPortalPaperProgression(portalKey: string, documentId: string): Promise<PortalProgressionResponse> {
    const response = await this.http.get(`/portal/${portalKey}/paper/${documentId}/progression`);
    return PortalProgressionResponseSchema.parse(response.data);
  }

  async updatePortalCompletedHighlights(
    portalKey: string,
    documentId: string,
    payload: UpdateCompletedHighlightsInput
  ): Promise<ShareDocument> {
    const response = await this.http.patch(
      `/portal/${portalKey}/paper/${documentId}/completed-highlights`,
      payload
    );
    return ShareDocumentSchema.parse(response.data);
  }

  // Portal Writing Mode API
  async getPortalAssignmentForWriting(
    portalKey: string,
    assignmentId: string
  ): Promise<PortalAssignmentWritingResponse> {
    const response = await this.http.get(
      `/portal/${portalKey}/assignments/${assignmentId}`
    );
    return PortalAssignmentWritingResponseSchema.parse(response.data);
  }

  async createPortalDocumentForWriting(
    portalKey: string,
    assignmentId: string
  ): Promise<PortalCreateDocumentResponse> {
    const response = await this.http.post(
      `/portal/${portalKey}/assignments/${assignmentId}/documents`
    );
    return PortalCreateDocumentResponseSchema.parse(response.data);
  }

  async updatePortalVersionContent(
    portalKey: string,
    documentId: string,
    versionId: string,
    payload: PortalVersionUpdateInput
  ): Promise<PortalDocumentVersion> {
    const response = await this.http.patch(
      `/portal/${portalKey}/documents/${documentId}/versions/${versionId}`,
      payload
    );
    return PortalDocumentVersionSchema.parse(response.data);
  }

  async submitPortalVersion(
    portalKey: string,
    documentId: string,
    versionId: string
  ): Promise<PortalVersionSubmitResponse> {
    const response = await this.http.post(
      `/portal/${portalKey}/documents/${documentId}/versions/${versionId}/submit`
    );
    return PortalVersionSubmitResponseSchema.parse(response.data);
  }

  async createPortalVersion(
    portalKey: string,
    documentId: string,
    payload: { parentVersionId?: string; bodyJson?: object }
  ): Promise<PortalDocumentVersion> {
    const response = await this.http.post(
      `/portal/${portalKey}/documents/${documentId}/versions`,
      payload
    );
    return PortalDocumentVersionSchema.parse(response.data);
  }

  async uploadEditEvents(
    portalKey: string,
    documentId: string,
    versionId: string,
    payload: EditEventBatchUploadInput
  ): Promise<EditEventBatchUploadResponse> {
    const response = await this.http.post(
      `/portal/${portalKey}/documents/${documentId}/versions/${versionId}/events`,
      payload
    );
    return EditEventBatchUploadResponseSchema.parse(response.data);
  }

  // Student Portal Key Management (authenticated - teacher endpoints)
  async regenerateStudentPortalKey(classId: string, studentId: string): Promise<Student> {
    const response = await this.http.post(
      `/classes/${classId}/students/${studentId}/regenerate-portal-key`,
      undefined,
      { headers: this.authHeaders() }
    );
    return StudentResponseSchema.parse(response.data);
  }

  async revokeStudentPortalKey(classId: string, studentId: string): Promise<Student> {
    const response = await this.http.delete(
      `/classes/${classId}/students/${studentId}/portal-key`,
      { headers: this.authHeaders() }
    );
    return StudentResponseSchema.parse(response.data);
  }

  // Edit Metrics (teacher view)
  async getDocumentEditMetrics(assignmentId: string, documentId: string): Promise<EditMetricsResponse> {
    const response = await this.http.get(
      `/assignments/${assignmentId}/documents/${documentId}/edit-metrics`,
      { headers: this.authHeaders() }
    );
    return EditMetricsResponseSchema.parse(response.data);
  }

  // Edit Events for replay (teacher view)
  async getDocumentEditEvents(assignmentId: string, documentId: string): Promise<ExtensionEditEventsResponse> {
    const response = await this.http.get(
      `/assignments/${assignmentId}/documents/${documentId}/edit-events`,
      { headers: this.authHeaders() }
    );
    return ExtensionEditEventsResponseSchema.parse(response.data);
  }

  // ==========================================================================
  // Extension Documents API (AI Detector)
  // ==========================================================================

  async listExtensionDocuments(params?: {
    limit?: number;
    offset?: number;
  }): Promise<ExtensionDocumentListResponse> {
    const response = await this.http.get("/extension/documents", {
      params,
      headers: this.authHeaders(),
    });
    return ExtensionDocumentListResponseSchema.parse(response.data);
  }

  async getExtensionDocument(documentId: string): Promise<ExtensionDocumentDetailResponse> {
    const response = await this.http.get(`/extension/documents/${documentId}`, {
      headers: this.authHeaders(),
    });
    return ExtensionDocumentDetailResponseSchema.parse(response.data);
  }

  async deleteExtensionDocument(documentId: string): Promise<void> {
    await this.http.delete(`/extension/documents/${documentId}`, {
      headers: this.authHeaders(),
    });
  }

  async deleteAllExtensionDocuments(): Promise<{ deletedCount: number }> {
    const response = await this.http.delete("/extension/documents", {
      headers: this.authHeaders(),
    });
    return DeleteCountResponseSchema.parse(response.data);
  }

  async exportExtensionData(): Promise<Blob> {
    const response = await this.http.get("/extension/export", {
      headers: this.authHeaders(),
      responseType: "blob",
    });
    return response.data as Blob;
  }

  async getExtensionDocumentEvents(documentId: string): Promise<ExtensionEditEventsResponse> {
    const response = await this.http.get(`/extension/documents/${documentId}/events`, {
      headers: this.authHeaders(),
    });
    return ExtensionEditEventsResponseSchema.parse(response.data);
  }

  async getExtensionLicenseStatus(): Promise<ExtensionLicenseStatusResponse> {
    const response = await this.http.get("/extension/license", {
      headers: this.authHeaders(),
    });
    return ExtensionLicenseStatusResponseSchema.parse(response.data);
  }
}

// Re-export types from shared for backward compatibility
export type {
  TypingMetrics,
  PasteMetrics,
  EditBehaviorMetrics,
  SessionMetrics,
  BurstMetrics,
  EditMetricsResponse,
  GCCourse,
  GCClassOut,
  GCSyncResult,
  GCStatus,
  RiskLevel,
  ExtensionDocumentSummary,
  ExtensionDocumentListResponse,
  ExtensionEditMetrics,
  ExtensionDocumentDetailResponse,
  ExtensionEditEvent,
  ExtensionEditEventsResponse,
  ExtensionLicenseStatusResponse,
} from "@markeroo/shared";
